import { useLocationPermissionStore } from "@/stores/location-permission-store";
import { useEffect, useState } from "react";

interface UserLocationHookReturn {
	userLocation: { lat: number; lng: number } | null;
	isLocationLoading: boolean;
	permissionStatus: "granted" | "denied" | "prompt" | null;
	requestLocation: () => Promise<void>;
}

/**
 * Custom hook for managing user location fetching and permissions
 * Handles geolocation permissions and coordinates retrieval
 */
export function useUserLocation(): UserLocationHookReturn {
	const [derivedLocation, setDerivedLocation] = useState<{ lat: number; lng: number } | null>(null);
	const [isLocationLoading, setIsLocationLoading] = useState(false);

	const { currentLocation, getCurrentLocation, permissionStatus, checkPermissionStatus } = useLocationPermissionStore();

	// Check permission status on mount
	useEffect(() => {
		if (permissionStatus === null) {
			checkPermissionStatus();
		}
	}, [permissionStatus, checkPermissionStatus]);

	// Auto-fetch location when permission is granted
	useEffect(() => {
		if (!currentLocation && permissionStatus === "granted") {
			setIsLocationLoading(true);
			getCurrentLocation()
				.then(position => {
					setDerivedLocation({
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					});
				})
				.catch(() => {
					// Handle error silently, location will remain null
				})
				.finally(() => {
					setIsLocationLoading(false);
				});
		}
	}, [currentLocation, getCurrentLocation, permissionStatus]);

	// Manual location request function
	const requestLocation = async (): Promise<void> => {
		setIsLocationLoading(true);
		try {
			const position = await getCurrentLocation();
			setDerivedLocation({
				lat: position.coords.latitude,
				lng: position.coords.longitude,
			});
		} catch (error) {
			throw error; // Let caller handle the error
		} finally {
			setIsLocationLoading(false);
		}
	};

	// Determine final location (store location takes precedence over derived)
	const userLocation = currentLocation
		? { lat: currentLocation.latitude, lng: currentLocation.longitude }
		: derivedLocation;

	return {
		userLocation,
		isLocationLoading,
		permissionStatus,
		requestLocation,
	};
}
